import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { CalendarPlus } from "lucide-react";
import { useAppDispatch } from "../../app/hooks";
import ModalShell from "../modal/ModalShell";
import { Member } from "../../features/members/models/Member";
import { SessionSearchResultResponse } from "../../features/search/contracts/SessionSearchResultResponse";
import { SessionOccurrence } from "../../features/sessions/models/SessionOccurrence";
import { OccurrenceStatus } from "../../features/sessions/models/OccurrenceStatus";
import { fetchSessionOccurrences } from "../../features/sessions/slice-extensions/occurrences-slice-extension";
import {
  bookOnBehalf,
  SeatApiError,
  searchSessionsToBook,
} from "../../features/sessions/slice-extensions/seats-slice-extension";
import { formatSeatDateTime } from "../../features/sessions/utils/seatDateTime";
import {
  fetchMemberUpcomingSeats,
  fetchSessionForBooking,
} from "../../features/sessions/slice-extensions/occupancy-slice-extension";
import { heldSeatDates, isOnSessionRoster } from "../../features/sessions/utils/memberHeldDates";
import { useVocabulary } from "../../features/organization/hooks/useVocabulary";

interface BookOnBehalfDialogProps {
  member: Member;
}

/** How far ahead the date picker lists a Session's occurrences. */
const DATE_WINDOW_DAYS = 56;

const isoDay = (date: Date): string => date.toISOString().slice(0, 10);

/**
 * "Book a date" on the member page: pick a Session by title, then one of its upcoming
 * dates, then Book. Staff bookings confirm at once; the 409s read "Already on that date"
 * or "Full". Dates already full read "Full", and dates the member already holds (a seat,
 * or every date when they are on the roster) read "Booked"; neither can be picked. The
 * server check stays the guard. A booking refreshes the member page's booked dates.
 */
const BookOnBehalfDialog = ({ member }: BookOnBehalfDialogProps) => {
  const { t, i18n } = useTranslation();
  const vocabulary = useVocabulary();
  const dispatch = useAppDispatch();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<SessionSearchResultResponse[]>([]);
  const [session, setSession] = useState<SessionSearchResultResponse | null>(null);
  const [dates, setDates] = useState<SessionOccurrence[]>([]);
  const [date, setDate] = useState("");
  const [held, setHeld] = useState<{ all: boolean; dates: Set<string> }>({ all: false, dates: new Set() });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || session || query.trim().length === 0) {
      setResults([]);
      return;
    }
    const timer = setTimeout(async () => {
      const result = await dispatch(searchSessionsToBook(query.trim()));
      if (searchSessionsToBook.fulfilled.match(result)) setResults(result.payload);
    }, 250);
    return () => clearTimeout(timer);
  }, [dispatch, open, query, session]);

  const close = () => {
    setOpen(false);
    setQuery("");
    setSession(null);
    setDates([]);
    setDate("");
    setHeld({ all: false, dates: new Set() });
    setError(null);
  };

  const isHeld = (o: SessionOccurrence): boolean => held.all || held.dates.has(o.effectiveDate);

  const pickSession = async (picked: SessionSearchResultResponse) => {
    setSession(picked);
    setQuery(picked.title);
    setError(null);
    const today = new Date();
    const until = new Date(today.getTime() + DATE_WINDOW_DAYS * 24 * 60 * 60 * 1000);
    const [result, details, seats] = await Promise.all([
      dispatch(fetchSessionOccurrences({ bookingId: picked.id, from: isoDay(today), to: isoDay(until) })),
      dispatch(fetchSessionForBooking(picked.id)),
      dispatch(fetchMemberUpcomingSeats(member.id)),
    ]);
    const nowHeld = {
      all: fetchSessionForBooking.fulfilled.match(details) && isOnSessionRoster(details.payload, member),
      dates: fetchMemberUpcomingSeats.fulfilled.match(seats) ? heldSeatDates(seats.payload, picked.id) : new Set<string>(),
    };
    const upcoming = fetchSessionOccurrences.fulfilled.match(result)
      ? result.payload.occurrences.filter(
          (o) => o.status !== OccurrenceStatus.Cancelled && o.effectiveDate >= isoDay(today),
        )
      : [];
    setDates(upcoming);
    setHeld(nowHeld);
    const free = upcoming.find((o) => !o.isFull && !nowHeld.all && !nowHeld.dates.has(o.effectiveDate));
    setDate(free?.effectiveDate ?? "");
  };

  const errorText = (payload: SeatApiError | undefined): string => {
    if (payload?.code === "full") return t("members.bookOnBehalf.full");
    if (payload?.code === "duplicate") return t("members.bookOnBehalf.alreadyBooked");
    if (payload?.statusCode === 404) return t("members.bookOnBehalf.notFound");
    return payload?.message || t("members.bookOnBehalf.failed");
  };

  const submit = async () => {
    if (!session || !date) return;
    setBusy(true);
    setError(null);
    const result = await dispatch(
      bookOnBehalf({ bookingId: session.id, request: { memberId: member.id, occurrenceDate: date } }),
    );
    setBusy(false);
    if (bookOnBehalf.fulfilled.match(result)) {
      toast.success(t("members.bookOnBehalf.success"));
      dispatch(fetchMemberUpcomingSeats(member.id));
      close();
    } else {
      setError(errorText(result.payload));
    }
  };

  return (
    <>
      <button type="button" className="btn btn-secondary flex items-center gap-1.5" onClick={() => setOpen(true)}>
        <CalendarPlus size={18} />
        {t("members.bookOnBehalf.action")}
      </button>
      {open && (
        <ModalShell onClose={close} labelledBy="book-on-behalf-title">
          <div className="p-4 lg:p-6">
            <h3 id="book-on-behalf-title" className="text-grey-10 dark:text-grey-90">
              {t("members.bookOnBehalf.title")}
            </h3>
            <p className="mt-1 text-sm text-grey-30 dark:text-grey-70">
              {`${member.firstName} ${member.lastName}`.trim()}
            </p>

            <div className="mt-4 space-y-3">
              <div>
                <label htmlFor="book-on-behalf-session" className="mb-1 block text-sm text-grey-20 dark:text-grey-80">
                  {t("members.bookOnBehalf.pickSession", vocabulary)}
                </label>
                <input
                  id="book-on-behalf-session"
                  type="text"
                  className="input"
                  value={query}
                  placeholder={t("members.bookOnBehalf.searchPlaceholder")}
                  onChange={(e) => {
                    setQuery(e.target.value);
                    setSession(null);
                    setDates([]);
                    setDate("");
                  }}
                />
                {!session && query.trim().length > 0 && (
                  <ul className="mt-1 flex flex-col gap-1">
                    {results.length === 0 ? (
                      <li className="px-3 py-1 text-sm text-grey-40 dark:text-grey-80">
                        {t("members.bookOnBehalf.noResults")}
                      </li>
                    ) : (
                      results.map((r) => (
                        <li key={r.id}>
                          <button
                            type="button"
                            className="w-full rounded-md px-3 py-1 text-left text-sm text-grey-20 hover:bg-grey-95 dark:text-white dark:hover:bg-primary-20"
                            onClick={() => pickSession(r)}
                          >
                            {r.title}
                          </button>
                        </li>
                      ))
                    )}
                  </ul>
                )}
              </div>

              {session && (
                <div>
                  <label htmlFor="book-on-behalf-date" className="mb-1 block text-sm text-grey-20 dark:text-grey-80">
                    {t("members.bookOnBehalf.pickDate")}
                  </label>
                  {dates.length === 0 ? (
                    <p className="text-sm text-grey-40 dark:text-grey-80">{t("members.bookOnBehalf.noDates")}</p>
                  ) : (
                    <select
                      id="book-on-behalf-date"
                      className="input"
                      value={date}
                      onChange={(e) => setDate(e.target.value)}
                    >
                      {dates.map((o) => (
                        <option
                          key={`${o.originalDate}-${o.effectiveDate}`}
                          value={o.effectiveDate}
                          disabled={o.isFull || isHeld(o)}
                        >
                          {formatSeatDateTime(o.effectiveDate, o.effectiveTime, i18n.language)}
                          {isHeld(o)
                            ? ` · ${t("seats.statusConfirmed")}`
                            : o.isFull
                              ? ` · ${t("members.bookOnBehalf.full")}`
                              : ""}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
              )}

              {error && (
                <p role="alert" className="text-sm text-red-700 dark:text-red-400">
                  {error}
                </p>
              )}
            </div>

            <div className="mt-5 flex justify-end gap-3">
              <button type="button" className="btn btn-secondary" onClick={close} disabled={busy}>
                {t("common.cancel")}
              </button>
              <button type="button" className="btn btn-primary" onClick={submit} disabled={busy || !session || !date}>
                {t("members.bookOnBehalf.submit")}
              </button>
            </div>
          </div>
        </ModalShell>
      )}
    </>
  );
};

export default BookOnBehalfDialog;
